import { Card } from "@/ingot";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateCartNote } from "@/api/queries";
import type { Cart } from "@/api/types";

/**
 * Customer-typed order note rendered below the operation sections in
 * the cart's left column ("pod výrobními operacemi" per the original
 * UX brief). Distinct from per-variant notes (which live on each
 * configurator → ``OrderLine.customer_note``); this one travels on
 * ``Order.customer_note`` and surfaces in the tenant admin order
 * detail above the parts table.
 *
 * Debounced auto-save: the textarea is the source of truth while the
 * user types; we flush to BE 500 ms after the last keystroke and patch
 * the cart cache so a reload shows the saved value without a refetch.
 *
 * ``lastSyncedRef`` tracks the BE-confirmed value (initial mount,
 * PATCH response, or an unrelated background refetch). The sync
 * effect only adopts a fresh remote value when the textarea still
 * matches what we last synced — protects an in-flight edit from
 * being clobbered by a background invalidation (e.g. an unrelated
 * ``useAddCartLine`` firing while the user is mid-edit).
 */
export function CartOrderNoteSection({
  cart,
}: {
  cart: Cart;
}): JSX.Element {
  const { t } = useTranslation("cart");
  const updateCartNote = useUpdateCartNote();

  const [orderNote, setOrderNote] = useState<string>(
    () => cart.customer_note ?? "",
  );
  const lastSyncedRef = useRef<string>(cart.customer_note ?? "");

  useEffect(() => {
    const remote = cart.customer_note ?? "";
    if (remote === lastSyncedRef.current) return;
    setOrderNote((local) =>
      local === lastSyncedRef.current ? remote : local,
    );
    lastSyncedRef.current = remote;
  }, [cart.customer_note]);

  useEffect(() => {
    const trimmed = orderNote.trim();
    const remote = (cart.customer_note ?? "").trim();
    if (trimmed === remote) return;
    const handle = window.setTimeout(() => {
      updateCartNote.mutate(trimmed === "" ? null : trimmed, {
        onSuccess: (data) => {
          lastSyncedRef.current = data.customer_note ?? "";
        },
      });
    }, 500);
    return () => window.clearTimeout(handle);
    // updateCartNote is stable from useMutation; intentionally omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNote, cart.customer_note]);

  return (
    <Card padded data-testid="cart-order-note-section">
      <label
        htmlFor="cart-order-note-textarea"
        className="mono block text-[11px] uppercase tracking-[0.08em] text-ink-3"
      >
        {t("orderNote.title", { defaultValue: "Poznámka k objednávce" })}
      </label>
      <p className="mt-1 text-xs text-ink-3">
        {t("orderNote.hint", {
          defaultValue:
            "Týká se celé objednávky a uvidí ji obsluha výroby.",
        })}
      </p>
      <textarea
        id="cart-order-note-textarea"
        value={orderNote}
        maxLength={2048}
        rows={4}
        onChange={(e) => setOrderNote(e.target.value)}
        placeholder={t("orderNote.placeholder", {
          defaultValue: "Cokoli, co bychom měli vědět k této objednávce…",
        })}
        className="mt-3 w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-ink shadow-sm placeholder:italic placeholder:text-ink-5 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </Card>
  );
}
